import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSession } from 'src/contexts/SessionContext';
import { Location as LocationType } from 'src/models/location/_types';
import { fetchLocations, updateLocation } from 'src/models/location/location';
import {
  PosConfiguration,
  PosConfigurationItem,
  UpdatePosConfigurationPayload,
} from 'src/models/pos-configuration/_types';
import {
  createPosConfiguration,
  deletePosConfiguration,
  fetchPosConfigurations,
  updatePosConfiguration,
} from 'src/models/pos-configuration/pos-configuration';
import { Identifier } from 'src/types/utilityTypes';
import toast from 'src/utils/toast';

export type PosType = 'pos' | 'kiosk';

export interface PosConfig {
  id: string;
  name: string;
  items: Tile[][];
}

export interface Tile {
  id: string;
  name: string;
  type: 'collection' | 'item' | 'discount';
  itemType: 'package' | 'product' | 'group' | 'plan';
  itemId: number;
  filterParams: string;
}

export interface PosPreviewConfig {
  posType: PosType;
  theme: string;
  viewMode: string;
  posConfig: PosConfiguration | null;
  posConfigs: PosConfiguration[] | null;
  selectedPage: number;
  isUpdatingConfig: boolean;
  selectedLocations: Identifier[];
  locations: any[] | undefined;
  isLoadingLocations: boolean;
  getTemplates: () => { label: string; value: string }[];
  setPosConfig: (posConfig: PosConfiguration) => void;
  setSelectedPage: (page: number) => void;
  setSelectedLocations: (locations: Identifier[]) => void;
  handleToggleLocation: (locationId: Identifier) => void;
  handleThemeChange: (theme: string) => void;
  handleViewModeChange: (viewMode: string) => void;
  handleSaveConfig: (posConfig: PosConfiguration | null) => void;
  handleCreateConfig: (
    name: string,
    onCreated?: (config: PosConfiguration) => void
  ) => void;
  handleDeleteConfig: (id: string) => void;
  handleDuplicateConfig: (
    id: string,
    onDuplicated?: (config: PosConfiguration) => void
  ) => void;
  handleReorderTiles: (
    pageIndex: number,
    newOrderedItems: PosConfigurationItem[]
  ) => void;
  hasUnsavedChanges: boolean;
}

const PosPreviewConfigContext = createContext<PosPreviewConfig | undefined>(
  undefined
);

export const usePosPreviewConfig = () => {
  const context = useContext(PosPreviewConfigContext);
  if (!context) {
    throw new Error(
      'usePosPreviewConfig must be used within a PosPreviewConfigProvider'
    );
  }
  return context;
};

interface PosPreviewConfigProviderProps {
  children: React.ReactNode;
}

export const PosPreviewConfigProvider = ({
  children,
}: PosPreviewConfigProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams<{ posType: PosType }>();
  const queryClient = useQueryClient();
  const theme = searchParams.get('theme') || 'light';
  const viewMode = searchParams.get('viewMode') || 'desktop';
  const posType = params.posType || 'pos';
  const [posConfig, setPosConfig] = useState<PosConfiguration | null>(null);
  const [selectedPage, setSelectedPage] = useState(0);
  const [selectedLocations, setSelectedLocations] = useState<Identifier[]>([]);
  const { session } = useSession();
  const { t } = useTranslation('translation', { keyPrefix: 'posPreview' });

  const { data: posConfigs, isLoading } = useQuery({
    queryKey: ['pos-config', posType],
    queryFn: () => fetchPosConfigurations(),
  });

  const { data: locations, isLoading: isLoadingLocations } = useQuery({
    queryKey: ['locations'],
    queryFn: () => fetchLocations({ fetchAll: true }),
    staleTime: 60000,
    gcTime: 60000,
    select: (data) =>
      data.locations.sort((a, b) => a.name.localeCompare(b.name)),
  });

  // Initialize all locations as selected when locations are loaded
  useEffect(() => {
    if (locations && locations.length > 0) {
      setSelectedLocations(
        locations
          .filter((location) =>
            location.posConfigurations?.some(
              (config) => config.id === posConfig?.id
            )
          )
          .map((location) => location.id)
      );
    }
  }, [locations, posConfig?.id]);

  useEffect(() => {
    if (posConfigs && !isLoading) {
      const filteredData = posConfigs.posConfigurations.filter(
        (config) => config.posType === posType
      );

      if (posConfig?.id) {
        const foundConfig =
          filteredData.find((config) => config.id === posConfig.id) || null;
        setPosConfig(foundConfig);
      } else {
        setPosConfig(
          filteredData.length > 0 ? filteredData[filteredData.length - 1] : null
        );
      }
    }
  }, [posConfigs, isLoading, posConfig?.id, posType]);

  const { mutate: createPosConfig } = useMutation({
    mutationFn: ({
      name,
      config,
      onCreated,
    }: {
      name: string;
      config?: PosConfigurationItem[][];
      onCreated?: (config: PosConfiguration) => void;
    }) =>
      createPosConfiguration({
        name,
        posType,
        config: config || [],
        platformId: session?.id || '',
      }).then((data) => {
        if (onCreated) {
          onCreated(data);
        } else {
          setPosConfig(data);
        }
        return data;
      }),
    onSuccess: () => {
      toast.success(t('templateCreatedSuccess'));
      queryClient.invalidateQueries({ queryKey: ['pos-config', posType] });
    },
  });

  const { mutate: updatePosConfig, isPending: isUpdatingConfig } = useMutation({
    mutationFn: (payload: UpdatePosConfigurationPayload) =>
      updatePosConfiguration({
        ...payload,
      }),
    onSuccess: () => {
      toast.success(t('templateUpdatedSuccess'));
      queryClient.invalidateQueries({ queryKey: ['pos-config', posType] });
    },
  });

  const { mutate: deletePosConfig } = useMutation({
    mutationFn: (id: string) => deletePosConfiguration(id),
    onSuccess: () => {
      toast.success(t('templateDeletedSuccess'));
      setPosConfig(null);
      queryClient.invalidateQueries({ queryKey: ['pos-config', posType] });
    },
  });

  const handleThemeChange = useCallback(
    (newTheme: string) => {
      if (!newTheme) return;
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('theme', newTheme);
        return params;
      });
    },
    [setSearchParams]
  );

  const handleViewModeChange = useCallback(
    (newViewMode: string) => {
      if (!newViewMode) return;
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('viewMode', newViewMode);
        return params;
      });
    },
    [setSearchParams]
  );

  const getTemplates = useCallback(() => {
    return (
      posConfigs?.posConfigurations
        .filter((config) => {
          return config.posType === posType;
        })
        .map((config) => ({
          label: config.name,
          value: config.id.toString(),
        })) || []
    );
  }, [posConfigs?.posConfigurations, posType]);

  // Compare the saved config with the current config to check if there are any unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    const savedConfig = posConfigs?.posConfigurations.find(
      (config) => config.id.toString() === posConfig?.id?.toString()
    )?.config;
    const currentConfig = posConfig?.config;
    return !isEqual(savedConfig, currentConfig);
  }, [posConfig, posConfigs?.posConfigurations]);

  const handleCreateConfig = (
    name: string,
    onCreated?: (config: PosConfiguration) => void
  ) => {
    createPosConfig({ name, config: [], onCreated });
  };

  const handleSaveConfig = (data: PosConfiguration | null) => {
    if (!data) return;
    updatePosConfig(data);
  };

  const handleDuplicateConfig = (
    id: string,
    onDuplicated?: (config: PosConfiguration) => void
  ) => {
    if (!id) return;
    const posConfig = posConfigs?.posConfigurations.find(
      (config) => config.id.toString() === id
    );
    if (!posConfig) return;

    createPosConfig({
      name: `${posConfig.name} (Copy)`,
      config: posConfig.config,
      onCreated: onDuplicated,
    });
  };

  const handleDeleteConfig = (id: string) => {
    if (!id) return;

    deletePosConfig(id);
  };

  const handleReorderTiles = useCallback(
    (pageIndex: number, newOrderedItems: PosConfigurationItem[]) => {
      if (!posConfig) return;

      const itemsWithPosition = newOrderedItems.map((item, index) => ({
        ...item,
        position: index,
      }));

      const newConfig = posConfig.config.map((page, index) => {
        if (index === pageIndex) {
          return itemsWithPosition;
        }
        return page;
      });

      const updatedConfig = {
        ...posConfig,
        config: newConfig,
      };

      setPosConfig(updatedConfig);

      const updatePayload = {
        id: posConfig.id,
        config: newConfig,
      };

      updatePosConfig(updatePayload, {
        onSuccess: () => {
          // no invalidate the query immediately to avoid conflicts
          // the local state is already updated
        },
        onError: (error) => {
          // eslint-disable-next-line no-console
          console.error('Update failed:', error);
        },
      });
    },
    [posConfig, updatePosConfig]
  );

  const { mutate: updatePosConfigLocations } = useMutation({
    mutationFn: (location: LocationType) =>
      updateLocation({
        ...location,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });

  const handleToggleLocation = useCallback(
    (locationId: Identifier) => {
      const location = locations?.find((l) => l.id === locationId);
      if (location && posConfig) {
        const posConfigIsSelected = location.posConfigurations?.some(
          (c) => c.id === posConfig?.id
        );

        const posConfigsIds =
          location.posConfigurations?.map((c) => c.id) || [];
        let newPosConfigsIds: Identifier[];

        if (posConfigIsSelected) {
          newPosConfigsIds = posConfigsIds.filter((id) => id !== posConfig?.id);
        } else {
          newPosConfigsIds = [...posConfigsIds, posConfig.id];
        }

        const payload = {
          ...location,
          posConfigurationIds: newPosConfigsIds,
        };

        updatePosConfigLocations({
          ...payload,
        });

        setSelectedLocations((prev) =>
          prev.includes(locationId)
            ? prev.filter((id) => id !== locationId)
            : [...prev, locationId]
        );
      }
    },
    [locations, posConfig, updatePosConfigLocations]
  );

  return (
    <PosPreviewConfigContext.Provider
      value={{
        posType,
        theme,
        viewMode,
        posConfig,
        selectedPage,
        isUpdatingConfig,
        selectedLocations,
        locations,
        isLoadingLocations,
        posConfigs: posConfigs?.posConfigurations || [],
        getTemplates,
        setPosConfig,
        setSelectedPage,
        setSelectedLocations,
        handleToggleLocation,
        handleThemeChange,
        handleViewModeChange,
        handleSaveConfig,
        handleCreateConfig,
        handleDeleteConfig,
        handleDuplicateConfig,
        handleReorderTiles,
        hasUnsavedChanges,
      }}
    >
      {children}
    </PosPreviewConfigContext.Provider>
  );
};
